import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  CloseIcon,
  MenuArrow,
  beds,
  prices,
  propertyTypes,
  statuses,
} from './lib';

export default function Filters({
  bedrooms,
  className,
  maxPrice,
  plan,
  propType,
  resetFilters,
  setActiveFilters,
  setBedrooms,
  setMaxPrice,
  setShowFilters,
  setPropType,
  setStatus,
  setReadyToMoveInto,
  showFilters,
  status,
  readyToMoveInto,
}) {
  // Helper function to see if a filter is active
  const isFilterActive = (value, items) => items.some((item) => item === value);

  // Toggle bedroom filters on and off
  const toggleBedrooms = (value) => {
    const bedroomActive = isFilterActive(value, bedrooms);

    if (bedroomActive) {
      const removeBedrooms = bedrooms.filter((bedroom) => bedroom !== value);
      setBedrooms(removeBedrooms);
    } else {
      setBedrooms([...bedrooms, value]);
    }
  };

  // Toggle property status filters on and off
  const toggleStatus = (value) => {
    const statusActive = isFilterActive(value, status);

    if (statusActive) {
      const removeStatus = status.filter((statusItem) => statusItem !== value);
      setStatus(removeStatus);
    } else {
      setStatus([...status, value]);
    }
  };

  // Toggle property type filters on and off
  const togglePropType = (value) => {
    const typeActive = isFilterActive(value, propType);

    if (typeActive) {
      const removeType = propType.filter((typeItem) => typeItem !== value);
      setPropType(removeType);
    } else {
      setPropType([...propType, value]);
    }
  };

  // Toggle maximum price filters on and off
  const toggleMaxPrice = (value) => {
    const priceActive = isFilterActive(value, maxPrice);

    if (priceActive) {
      const removePrice = maxPrice.filter((priceItem) => priceItem !== value);
      setMaxPrice(removePrice);
    } else {
      setMaxPrice([...maxPrice, value]);
    }
  };

  // Submit the filters form
  const submitFilters = (e) => {
    e.preventDefault();
    // Add each of the temporary state holders to an array
    const filtersToApply = [];

    if (bedrooms?.length > 0) {
      filtersToApply.bedrooms = bedrooms;
    }

    if (propType?.length > 0) {
      filtersToApply.type = propType;
    }

    if (status?.length > 0) {
      filtersToApply.status = status;
    }

    if (maxPrice?.length > 0) {
      filtersToApply.price = maxPrice;
    }

    if (readyToMoveInto) {
      filtersToApply.ready_to_move_into = true;
    }

    // Then set this new array to the "active filters" to filter the data by
    setActiveFilters(filtersToApply);
    setShowFilters(false);
  };

  return (
    <>
      <div
        className={`siteplan__filters${
          showFilters ? ' siteplan__filters--active' : ''
        }${className ? ` ${className}` : ''}`}
      >
        <div className="siteplan__filter-wrap">
          <h6 className="siteplan__filter-heading">Filter</h6>

          <button
            aria-label="Close"
            className="siteplan__close"
            onClick={() => setShowFilters(false)}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <form
          className="siteplan__filter-form"
          onSubmit={(e) => submitFilters(e)}
        >
          <Popover.Root>
            <div className="siteplan__trigger-wrap">
              <Popover.Trigger asChild>
                <button className="siteplan__filter-trigger" type="button">
                  <span className="siteplan__trigger-label">Status</span>
                  <MenuArrow className="siteplan__trigger-icon" />
                </button>
              </Popover.Trigger>
              <Popover.Anchor />
            </div>

            <Popover.Content
              align="start"
              className="siteplan__filter-items"
              avoidCollisions={false}
              side="bottom"
            >
              <fieldset
                className="siteplan__fieldset siteplan__fieldset--status"
                id="status"
              >
                {statuses.map((propStatus) => (
                  <div className="siteplan__filter-item" key={propStatus.key}>
                    <label
                      className="siteplan__filter"
                      htmlFor={propStatus.key}
                    >
                      <input
                        checked={isFilterActive(propStatus.key, status)}
                        className="siteplan__radio"
                        id={propStatus.key}
                        name="propStatus"
                        onChange={() => toggleStatus(propStatus.key)}
                        type="checkbox"
                        value={propStatus.key}
                      />

                      <span className="siteplan__text">{propStatus.value}</span>
                    </label>
                  </div>
                ))}
              </fieldset>
            </Popover.Content>
          </Popover.Root>

          {plan.type !== 'apartments' && (
            <Popover.Root>
              <div className="siteplan__trigger-wrap">
                <Popover.Trigger asChild>
                  <button className="siteplan__filter-trigger" type="button">
                    <span className="siteplan__trigger-label">House Style</span>
                    <MenuArrow className="siteplan__trigger-icon" />
                  </button>
                </Popover.Trigger>
                <Popover.Anchor />
              </div>

              <Popover.Content
                align="start"
                className="siteplan__filter-items"
                avoidCollisions={false}
                side="bottom"
              >
                <fieldset
                  className="siteplan__fieldset siteplan__fieldset--type"
                  id="houseType"
                >
                  {propertyTypes.map((type) => (
                    <label
                      className="siteplan__filter"
                      htmlFor={type.key}
                      key={type.key}
                    >
                      <input
                        checked={isFilterActive(type.value, propType)}
                        className="siteplan__radio"
                        id={type.key}
                        name="houseType"
                        onChange={() => togglePropType(type.value)}
                        type="checkbox"
                        value={type.value}
                      />

                      <span className="siteplan__text">{type.value}</span>
                    </label>
                  ))}
                </fieldset>
              </Popover.Content>
            </Popover.Root>
          )}

          <Popover.Root>
            <div className="siteplan__trigger-wrap">
              <Popover.Trigger asChild>
                <button className="siteplan__filter-trigger" type="button">
                  <span className="siteplan__trigger-label">Bedrooms</span>
                  <MenuArrow className="siteplan__trigger-icon" />
                </button>
              </Popover.Trigger>
              <Popover.Anchor />
            </div>

            <Popover.Content
              align="start"
              className="siteplan__filter-items"
              avoidCollisions={false}
              side="bottom"
            >
              <fieldset
                className="siteplan__fieldset siteplan__fieldset--bedrooms"
                id="bedrooms"
              >
                {beds.map((bedroom) => (
                  <label
                    className="siteplan__filter"
                    htmlFor={bedroom.key}
                    key={bedroom.key}
                  >
                    <input
                      checked={isFilterActive(bedroom.value, bedrooms)}
                      className="siteplan__radio"
                      id={bedroom.key}
                      name="bedrooms"
                      onChange={() => toggleBedrooms(bedroom.value)}
                      type="checkbox"
                      value={bedroom.value}
                    />

                    <span className="siteplan__text">{bedroom.value}</span>
                  </label>
                ))}
              </fieldset>
            </Popover.Content>
          </Popover.Root>

          <Popover.Root>
            <div className="siteplan__trigger-wrap">
              <Popover.Trigger asChild>
                <button className="siteplan__filter-trigger" type="button">
                  <span className="siteplan__trigger-label">Max Price</span>
                  <MenuArrow className="siteplan__trigger-icon" />
                </button>
              </Popover.Trigger>
              <Popover.Anchor />
            </div>

            <Popover.Content
              align="start"
              className="siteplan__filter-items"
              avoidCollisions={false}
              side="bottom"
            >
              <fieldset
                className="siteplan__fieldset siteplan__fieldset--prices"
                id="prices"
              >
                {prices.map((price) => (
                  <label
                    className="siteplan__filter"
                    htmlFor={price.key}
                    key={price.key}
                  >
                    <input
                      checked={isFilterActive(price.key, maxPrice)}
                      className="siteplan__radio"
                      id={price.key}
                      name="prices"
                      onChange={() => toggleMaxPrice(price.key)}
                      type="checkbox"
                      value={price.key}
                    />

                    <span className="siteplan__text">{price.value}</span>
                  </label>
                ))}
              </fieldset>
            </Popover.Content>
          </Popover.Root>

          <div className="siteplan__filter">
            <label
              className="siteplan__filter siteplan__filter--checkbox"
              htmlFor="ready-to-move-into-filter"
            >
              <input
                type="checkbox"
                id="ready-to-move-into-filter"
                checked={readyToMoveInto}
                onChange={() => setReadyToMoveInto(!readyToMoveInto)}
                className="siteplan__radio"
                value="ready-to-move-into"
              />
              <span className="siteplan__text">Ready to Move Into</span>
            </label>
          </div>

          <div className="siteplan__filters-buttons">
            <button
              className="button button--clear siteplan__reset"
              onClick={() => {
                resetFilters();
              }}
              type="button"
            >
              Reset
            </button>
            <button className="button siteplan__submit" type="submit">
              Update
            </button>
          </div>
        </form>
      </div>
      <button
        aria-label="Close filters"
        className={`siteplan__filter-overlay${
          showFilters ? ' siteplan__filter-overlay--active' : ''
        }`}
        onClick={() => setShowFilters(false)}
        type="button"
      />
    </>
  );
}
